import { Link } from 'react-router-dom';
import LegalPage, { LegalSection } from '../../components/legal/LegalPage';

const ulCls = 'list-disc space-y-1.5 pl-5';

export default function Terms() {
  return (
    <LegalPage
      title="Terms of Service"
      lastUpdated="May 22, 2026"
      intro={
        <p>
          These Terms of Service (&ldquo;Terms&rdquo;) govern your use of the Calendyz website and
          booking platform at <span className="text-fg-secondary font-medium">calendyz.com</span>.
          By creating an account or using Calendyz, you agree to these Terms.
        </p>
      }
    >
      <LegalSection heading="1. The service">
        <p>
          Calendyz is an online platform that connects customers with beauty and grooming salons.
          Customers can browse salons, book appointments, and leave reviews. Salons (&ldquo;salon
          owners&rdquo; or &ldquo;professionals&rdquo;) can list their business and manage
          appointments. Calendyz facilitates bookings but is not a party to the service performed by
          a salon.
        </p>
      </LegalSection>

      <LegalSection heading="2. Eligibility and accounts">
        <ul className={ulCls}>
          <li>You must be at least 16 years old to create an account.</li>
          <li>
            You must provide accurate information and keep it up to date. You may register with an
            email and password, or by signing in with Google.
          </li>
          <li>
            Each sign-in method creates a separate account. Calendyz does not merge accounts created
            with different methods.
          </li>
          <li>
            You are responsible for keeping your credentials secure and for all activity under your
            account. Notify us immediately of any unauthorized use.
          </li>
        </ul>
      </LegalSection>

      <LegalSection heading="3. Bookings">
        <ul className={ulCls}>
          <li>A verified account is required to book an appointment.</li>
          <li>
            A booking is a request for service from the chosen salon. The salon is solely
            responsible for the service, its pricing, quality, and for honoring or rescheduling the
            appointment.
          </li>
          <li>
            You agree to attend appointments you book or to cancel in good time. Cancellation,
            no-show, and rescheduling policies are set by each salon.
          </li>
          <li>
            Calendyz is not responsible for any dispute between you and a salon regarding a service.
          </li>
        </ul>
      </LegalSection>

      <LegalSection heading="4. Reviews">
        <p>
          You may submit a review only for a salon where you have a completed appointment. Reviews
          must reflect your genuine experience. You agree not to post content that is false,
          defamatory, abusive, unlawful, or that infringes the rights of others. We may remove
          reviews that violate these Terms. By submitting a review, you grant Calendyz a
          non-exclusive right to display it on the platform.
        </p>
      </LegalSection>

      <LegalSection heading="5. Salon owners">
        <p>If you register a salon on Calendyz, you additionally agree that:</p>
        <ul className={ulCls}>
          <li>You are authorized to represent the business you list.</li>
          <li>Your listing, services, prices, and availability are accurate and lawful.</li>
          <li>
            You will honor confirmed bookings and treat customers&rsquo; personal information in
            accordance with applicable law and our{' '}
            <Link to="/privacy" className="text-accent hover:underline">
              Privacy Policy
            </Link>
            .
          </li>
        </ul>
      </LegalSection>

      <LegalSection heading="6. Acceptable use">
        <p>You agree not to:</p>
        <ul className={ulCls}>
          <li>Use the platform for any unlawful or fraudulent purpose.</li>
          <li>Create accounts or bookings using false information or impersonate others.</li>
          <li>
            Attempt to gain unauthorized access to the platform, other accounts, or its systems.
          </li>
          <li>Interfere with, disrupt, scrape, or place undue load on the service.</li>
          <li>Submit fake or manipulated reviews.</li>
        </ul>
      </LegalSection>

      <LegalSection heading="7. Intellectual property">
        <p>
          The Calendyz name, logo, design, and software are owned by Calendyz and protected by
          intellectual-property laws. You may not copy, modify, or redistribute them without our
          permission. Content you submit (such as reviews) remains yours, but you grant us the right
          to display it as part of the service.
        </p>
      </LegalSection>

      <LegalSection heading="8. Disclaimers">
        <p>
          The platform is provided &ldquo;as is&rdquo; and &ldquo;as available&rdquo;. Calendyz does
          not provide beauty or grooming services and does not guarantee the quality, safety, or
          legality of any salon or service listed. We do not warrant that the platform will be
          uninterrupted or error-free.
        </p>
      </LegalSection>

      <LegalSection heading="9. Limitation of liability">
        <p>
          To the maximum extent permitted by law, Calendyz is not liable for any indirect,
          incidental, or consequential damages, or for any loss arising from a service performed (or
          not performed) by a salon, from a dispute between users, or from your inability to use the
          platform.
        </p>
      </LegalSection>

      <LegalSection heading="10. Termination">
        <p>
          You may delete your account at any time. We may suspend or terminate an account that
          violates these Terms or that we reasonably believe is being used for fraud or abuse.
        </p>
      </LegalSection>

      <LegalSection heading="11. Changes to these Terms">
        <p>
          We may update these Terms from time to time. When we do, we will revise the &ldquo;Last
          updated&rdquo; date above. Your continued use of Calendyz after changes take effect
          constitutes acceptance of the updated Terms.
        </p>
      </LegalSection>

      <LegalSection heading="12. Contact us">
        <p>
          Questions about these Terms? Email{' '}
          <a href="mailto:support@calendyz.com" className="text-accent hover:underline">
            support@calendyz.com
          </a>
          . See also our{' '}
          <Link to="/privacy" className="text-accent hover:underline">
            Privacy Policy
          </Link>
          .
        </p>
      </LegalSection>
    </LegalPage>
  );
}
